/**
 * src/lib/auto-filters.ts
 *
 * Automatic filter detection from SQL WHERE clauses.
 *
 * When a dashboard is created, this module parses the SQL queries in each widget
 * to extract columns used in WHERE conditions, then matches them against the
 * database schema to produce properly-typed ConfiguredFilter entries.
 *
 * Used by Chat.tsx and Dashboards.tsx during dashboard creation.
 */

import type { ConfiguredFilter, FilterControlType } from "@/components/FilterSuggestionsDialog";
import type { TableSchema, ColumnInfo } from "@/hooks/useMcpSchema";

// ─── SQL Parsing Helpers ────────────────────────────────────────────────────

/** Columns that are structural (joins, IDs) rather than user-facing filters. */
const SKIP_COLUMN_PATTERNS = [
  /^id$/i,
  /^uuid$/i,
  /^_id$/i,
  /^pk$/i,
  /^fk_/i,
  /^created_by$/i,
  /^updated_by$/i,
  /^deleted_at$/i,
  /^deleted_by$/i,
];

/** Column names that strongly suggest a dropdown/select filter. */
const DROPDOWN_NAME_PATTERNS = [
  /status/i,
  /type$/i,
  /^type_/i,
  /category/i,
  /state$/i,
  /level$/i,
  /priority/i,
  /role$/i,
  /plan$/i,
  /tier$/i,
  /channel$/i,
  /source$/i,
  /gender$/i,
  /country$/i,
  /region$/i,
  /department$/i,
];

/** Column names that suggest an email input. */
const EMAIL_NAME_PATTERNS = [/email/i, /e_mail/i, /mail_address/i];

/** Date-like column names (supplement to data-type detection). */
const DATE_NAME_PATTERNS = [
  /date/i,
  /time/i,
  /timestamp/i,
  /_at$/i,
  /_on$/i,
  /^created/i,
  /^updated/i,
  /^period/i,
];

// ─── Data-type detection (mirrors FilterSuggestionsDialog.detectFilterType) ──

const DATE_TYPE_PATTERNS = [/^date$/i, /^datetime$/i, /^timestamp/i, /^time$/i, /^year$/i];
const NUMBER_TYPE_PATTERNS = [
  /^int/i, /^integer$/i, /^bigint$/i, /^smallint$/i, /^tinyint$/i, /^mediumint$/i,
  /^decimal/i, /^numeric/i, /^float/i, /^double/i, /^real$/i, /^money$/i,
  /^serial$/i, /^bigserial$/i,
];
const BOOLEAN_TYPE_PATTERNS = [/^bool/i, /^bit$/i];
const ENUM_TYPE_PATTERNS = [/^enum/i, /^set$/i, /^user-defined$/i];

/**
 * Determine the best filter control type for a given column, using both the
 * SQL data type and the column name for heuristic refinement.
 */
export function detectFilterType(
  dataType: string,
  columnName: string,
): FilterControlType {
  // Data-type based detection first
  if (DATE_TYPE_PATTERNS.some((p) => p.test(dataType))) return "date";
  if (NUMBER_TYPE_PATTERNS.some((p) => p.test(dataType))) return "number";
  if (BOOLEAN_TYPE_PATTERNS.some((p) => p.test(dataType))) return "singleselect";
  if (ENUM_TYPE_PATTERNS.some((p) => p.test(dataType))) return "singleselect";

  // Column-name heuristics for text-like types
  if (DATE_NAME_PATTERNS.some((p) => p.test(columnName))) return "date";
  if (EMAIL_NAME_PATTERNS.some((p) => p.test(columnName))) return "text";
  if (DROPDOWN_NAME_PATTERNS.some((p) => p.test(columnName))) return "singleselect";

  // Default: multiselect with autocomplete
  return "multiselect";
}

// ─── SQL WHERE clause parser ────────────────────────────────────────────────

interface ExtractedColumn {
  tableName: string | null; // null if not qualified
  columnName: string;
}

/**
 * Extract the FROM table(s) from a SQL query.
 * Returns a map of alias → tableName.
 */
export function extractFromTables(sql: string): Map<string, string> {
  const tables = new Map<string, string>();

  // Match FROM clause: FROM table_name [alias]
  const fromMatch = sql.match(/\bFROM\s+["'`]?(\w+)["'`]?(?:\s+(?:AS\s+)?["'`]?(\w+)["'`]?)?/i);
  if (fromMatch) {
    const tableName = fromMatch[1];
    const alias = fromMatch[2] || tableName;
    tables.set(alias.toLowerCase(), tableName);
    tables.set(tableName.toLowerCase(), tableName);
  }

  // Match JOIN clauses: JOIN table_name [alias]
  const joinRegex = /\bJOIN\s+["'`]?(\w+)["'`]?(?:\s+(?:AS\s+)?["'`]?(\w+)["'`]?)?/gi;
  let match;
  while ((match = joinRegex.exec(sql)) !== null) {
    const tableName = match[1];
    const alias = match[2] || tableName;
    tables.set(alias.toLowerCase(), tableName);
    tables.set(tableName.toLowerCase(), tableName);
  }

  return tables;
}

/**
 * Extract column references from a SQL WHERE clause.
 *
 * Handles common patterns:
 *   col = 'value', col > 5, col BETWEEN x AND y,
 *   col IN (...), col LIKE '...', col IS NULL, table.col = ...
 */
function extractWhereColumns(sql: string): ExtractedColumn[] {
  // Extract the WHERE clause (everything after WHERE until GROUP BY, ORDER BY, LIMIT, HAVING, or end)
  const whereMatch = sql.match(
    /\bWHERE\s+([\s\S]+?)(?:\bGROUP\s+BY\b|\bORDER\s+BY\b|\bLIMIT\b|\bHAVING\b|\bUNION\b|;|$)/i
  );
  if (!whereMatch) return [];

  const whereClause = whereMatch[1];
  const columns: ExtractedColumn[] = [];
  const seen = new Set<string>();

  // Pattern: [table.]column <operator> value
  // Captures optional table prefix and column name before operators
  const conditionRegex =
    /(?:^|AND|OR)\s*["'`]?(?:(\w+)\.)?["'`]?["'`]?(\w+)["'`]?\s*(?:=|!=|<>|>=?|<=?|LIKE|ILIKE|IN\s*\(|IS\s+|BETWEEN|NOT\s+IN)/gi;

  let m;
  while ((m = conditionRegex.exec(whereClause)) !== null) {
    const tableRef = m[1] || null;
    const colName = m[2];

    // Skip SQL keywords that might match
    if (/^(AND|OR|NOT|NULL|TRUE|FALSE|NOW|CURRENT|DATE|INTERVAL|CASE|WHEN|THEN|ELSE|END)$/i.test(colName)) {
      continue;
    }

    const key = `${(tableRef || "").toLowerCase()}.${colName.toLowerCase()}`;
    if (!seen.has(key)) {
      seen.add(key);
      columns.push({ tableName: tableRef, columnName: colName });
    }
  }

  return columns;
}

// ─── WHERE clause extraction WITH values ────────────────────────────────────

export interface ExtractedWhereParam {
  tableName: string | null;
  columnName: string;
  operator: string;
  /** Raw value as it appears in SQL (including quotes). */
  rawValue: string;
  /** Cleaned value (without surrounding quotes). */
  value: string;
  /** Full matched substring for replacement. */
  fullMatch: string;
}

/**
 * Extract column references AND their compared values from a SQL WHERE clause.
 *
 * Returns an array of { columnName, operator, value, fullMatch } objects.
 * The fullMatch can be used for targeted string replacement in the SQL.
 */
export function extractWhereParams(sql: string): ExtractedWhereParam[] {
  const whereMatch = sql.match(
    /\bWHERE\s+([\s\S]+?)(?:\bGROUP\s+BY\b|\bORDER\s+BY\b|\bLIMIT\b|\bHAVING\b|\bUNION\b|;|$)/i
  );
  if (!whereMatch) return [];

  const whereClause = whereMatch[1];
  const params: ExtractedWhereParam[] = [];
  const seen = new Set<string>();

  // Pattern: [table.]column operator value
  // value can be: 'string', "string", number, function call (e.g. NOW()), or date keyword (e.g. CURRENT_DATE)
  const conditionRegex =
    /["'`]?(?:(\w+)\.)?["'`]?["'`]?(\w+)["'`]?\s*(=|!=|<>|>=?|<=?|LIKE|ILIKE|NOT\s+LIKE|NOT\s+ILIKE)\s*('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?|\w+\s*\([^)]*\)(?:\s*[-+]\s*INTERVAL\s+'[^']*'(?:\s+\w+)?)?|CURRENT_\w+(?:\s*[-+]\s*INTERVAL\s+'[^']*'(?:\s+\w+)?)?)/gi;

  let m;
  while ((m = conditionRegex.exec(whereClause)) !== null) {
    const tableRef = m[1] || null;
    const colName = m[2];
    const operator = m[3];
    const rawValue = m[4];

    if (/^(AND|OR|NOT|NULL|TRUE|FALSE|NOW|CURRENT|DATE|INTERVAL|CASE|WHEN|THEN|ELSE|END)$/i.test(colName)) {
      continue;
    }

    // Strip surrounding quotes from value; use empty string for function calls / date keywords
    let value = rawValue;
    if ((value.startsWith("'") && value.endsWith("'")) || (value.startsWith('"') && value.endsWith('"'))) {
      value = value.slice(1, -1);
    } else if (/^\w+\s*\(|^CURRENT_/i.test(value)) {
      // Function call or SQL date keyword – not a useful literal default
      value = "";
    }

    const key = `${(tableRef || "").toLowerCase()}.${colName.toLowerCase()}`;
    if (!seen.has(key)) {
      seen.add(key);
      params.push({
        tableName: tableRef,
        columnName: colName,
        operator,
        rawValue,
        value,
        fullMatch: m[0],
      });
    }
  }

  // Also extract BETWEEN patterns: col BETWEEN val1 AND val2
  const betweenRegex =
    /["'`]?(?:(\w+)\.)?["'`]?["'`]?(\w+)["'`]?\s+BETWEEN\s+('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?|\w+\s*\([^)]*\)(?:\s*[-+]\s*INTERVAL\s+'[^']*'(?:\s+\w+)?)?|CURRENT_\w+(?:\s*[-+]\s*INTERVAL\s+'[^']*'(?:\s+\w+)?)?)\s+AND\s+('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?|\w+\s*\([^)]*\)(?:\s*[-+]\s*INTERVAL\s+'[^']*'(?:\s+\w+)?)?|CURRENT_\w+(?:\s*[-+]\s*INTERVAL\s+'[^']*'(?:\s+\w+)?)?)/gi;

  while ((m = betweenRegex.exec(whereClause)) !== null) {
    const tableRef = m[1] || null;
    const colName = m[2];

    if (/^(AND|OR|NOT|NULL|TRUE|FALSE|NOW|CURRENT|DATE|INTERVAL|CASE|WHEN|THEN|ELSE|END)$/i.test(colName)) {
      continue;
    }

    const key = `${(tableRef || "").toLowerCase()}.${colName.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);

    let val1 = m[3];
    if ((val1.startsWith("'") && val1.endsWith("'")) || (val1.startsWith('"') && val1.endsWith('"'))) {
      val1 = val1.slice(1, -1);
    } else if (/^\w+\s*\(|^CURRENT_/i.test(val1)) {
      val1 = "";
    }

    params.push({
      tableName: tableRef,
      columnName: colName,
      operator: "BETWEEN",
      rawValue: m[3],
      value: val1,
      fullMatch: m[0],
    });
  }

  // Also extract IN (...) patterns: col IN ('val1', 'val2', ...)
  const inRegex =
    /["'`]?(?:(\w+)\.)?["'`]?["'`]?(\w+)["'`]?\s+(IN|NOT\s+IN)\s*\(([^)]+)\)/gi;

  while ((m = inRegex.exec(whereClause)) !== null) {
    const tableRef = m[1] || null;
    const colName = m[2];
    const operator = m[3];
    const inList = m[4].trim();

    if (/^(AND|OR|NOT|NULL|TRUE|FALSE|NOW|CURRENT|DATE|INTERVAL|CASE|WHEN|THEN|ELSE|END)$/i.test(colName)) {
      continue;
    }

    const key = `${(tableRef || "").toLowerCase()}.${colName.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);

    // Parse the first value from the IN list as default
    const firstValMatch = inList.match(/'([^']*)'|"([^"]*)"|(-?\d+(?:\.\d+)?)/);
    const value = firstValMatch ? (firstValMatch[1] ?? firstValMatch[2] ?? firstValMatch[3] ?? "") : inList;

    params.push({
      tableName: tableRef,
      columnName: colName,
      operator,
      rawValue: inList,
      value,
      fullMatch: m[0],
    });
  }

  return params;
}

/**
 * Map a FilterControlType to the placeholder type string used in {{name|type|default}}.
 */
function filterTypeToPlaceholderType(ft: FilterControlType): string {
  switch (ft) {
    case "date": return "date";
    case "number": return "number";
    case "text": return "text";
    case "singleselect": return "dropdown";
    case "multiselect": return "multiselect";
    default: return "text";
  }
}

/**
 * Replace hardcoded WHERE values with {{placeholder|type|default}} in a SQL query.
 *
 * For each replacement entry, finds the matching condition in the WHERE clause
 * and swaps the literal value with the placeholder syntax.
 */
export function replaceWhereValuesWithPlaceholders(
  sql: string,
  replacements: Array<{
    columnName: string;
    tableName: string | null;
    filterType: FilterControlType;
    label: string;
    defaultValue: string;
  }>,
): string {
  let result = sql;

  for (const rep of replacements) {
    const paramName = rep.columnName;
    const phType = filterTypeToPlaceholderType(rep.filterType);
    const placeholder = `{{${paramName}|${phType}|${rep.defaultValue}}}`;

    // Build regex to find the condition involving this column and replace the value part
    const colEscaped = rep.columnName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const tablePrefix = rep.tableName
      ? `(?:["'\`]?${rep.tableName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}["'\`]?\\.)?`
      : `(?:["'\`]?\\w+["'\`]?\\.)?`;

    // Match: [table.]column operator 'value' or number
    const valuePattern = new RegExp(
      `(${tablePrefix}["'\`]?${colEscaped}["'\`]?\\s*(?:=|!=|<>|>=?|<=?|LIKE|ILIKE|NOT\\s+LIKE|NOT\\s+ILIKE)\\s*)('(?:[^'\\\\]|\\\\.)*'|"(?:[^"\\\\]|\\\\.)*"|-?\\d+(?:\\.\\d+)?)`,
      "gi"
    );

    result = result.replace(valuePattern, `$1${placeholder}`);

    // Also handle IN(...) patterns - replace the whole value list
    const inPattern = new RegExp(
      `(${tablePrefix}["'\`]?${colEscaped}["'\`]?\\s+(?:IN|NOT\\s+IN)\\s*\\()([^)]+)(\\))`,
      "gi"
    );

    result = result.replace(inPattern, `$1${placeholder}$3`);

    // Handle BETWEEN: col BETWEEN val1 AND val2 → col BETWEEN {{col_start|type|val1}} AND {{col_end|type|val2}}
    const betweenPattern = new RegExp(
      `(${tablePrefix}["'\`]?${colEscaped}["'\`]?\\s+BETWEEN\\s+)('(?:[^'\\\\]|\\\\.)*'|"(?:[^"\\\\]|\\\\.)*"|-?\\d+(?:\\.\\d+)?)(\\s+AND\\s+)('(?:[^'\\\\]|\\\\.)*'|"(?:[^"\\\\]|\\\\.)*"|-?\\d+(?:\\.\\d+)?)`,
      "gi"
    );

    result = result.replace(betweenPattern, (_, prefix, val1, andPart, val2) => {
      let cleanVal1 = val1;
      let cleanVal2 = val2;
      if ((cleanVal1.startsWith("'") && cleanVal1.endsWith("'")) || (cleanVal1.startsWith('"') && cleanVal1.endsWith('"'))) {
        cleanVal1 = cleanVal1.slice(1, -1);
      }
      if ((cleanVal2.startsWith("'") && cleanVal2.endsWith("'")) || (cleanVal2.startsWith('"') && cleanVal2.endsWith('"'))) {
        cleanVal2 = cleanVal2.slice(1, -1);
      }
      return `${prefix}{{${paramName}_start|${phType}|${cleanVal1}}}${andPart}{{${paramName}_end|${phType}|${cleanVal2}}}`;
    });
  }

  return result;
}

// ─── Public API ─────────────────────────────────────────────────────────────

interface WidgetWithQuery {
  sqlQuery?: string;
  title?: string;
}

/**
 * Human-readable label from a column name: snake_case → Title Case.
 */
function toLabel(name: string): string {
  return name
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Auto-detect filters from the SQL queries in dashboard widgets.
 *
 * For each widget:
 *  1. Parse the WHERE clause to find referenced columns
 *  2. Resolve the table name from the FROM/JOIN clause or schema
 *  3. Look up the column data type from the schema
 *  4. Determine the appropriate filter control type
 *  5. Skip structural columns (IDs, foreign keys, etc.)
 *
 * Returns de-duplicated ConfiguredFilter[] ready to use.
 */
export function buildAutoFilters(
  widgets: WidgetWithQuery[],
  schemaData: TableSchema[],
): ConfiguredFilter[] {
  if (schemaData.length === 0) return [];

  // Build lookup: table.column → ColumnInfo
  const columnLookup = new Map<string, ColumnInfo & { tableName: string }>();
  for (const t of schemaData) {
    for (const c of t.columns) {
      columnLookup.set(
        `${t.table_name.toLowerCase()}.${c.column_name.toLowerCase()}`,
        { ...c, tableName: t.table_name },
      );
    }
  }

  // Also build a reverse lookup: columnName → possible tables (for unqualified refs)
  const columnToTables = new Map<string, { tableName: string; col: ColumnInfo }[]>();
  for (const t of schemaData) {
    for (const c of t.columns) {
      const key = c.column_name.toLowerCase();
      if (!columnToTables.has(key)) columnToTables.set(key, []);
      columnToTables.get(key)!.push({ tableName: t.table_name, col: c });
    }
  }

  const filters: ConfiguredFilter[] = [];
  const seen = new Set<string>(); // table.column dedup key

  for (const widget of widgets) {
    if (!widget.sqlQuery) continue;

    const fromTables = extractFromTables(widget.sqlQuery);
    const whereColumns = extractWhereColumns(widget.sqlQuery);

    for (const ref of whereColumns) {
      // Skip structural columns
      if (SKIP_COLUMN_PATTERNS.some((p) => p.test(ref.columnName))) continue;

      let resolvedTable: string | null = null;
      let colInfo: ColumnInfo | null = null;

      if (ref.tableName) {
        // Qualified reference — resolve alias
        const realTable = fromTables.get(ref.tableName.toLowerCase()) || ref.tableName;
        const lookup = columnLookup.get(`${realTable.toLowerCase()}.${ref.columnName.toLowerCase()}`);
        if (lookup) {
          resolvedTable = lookup.tableName;
          colInfo = lookup;
        }
      } else {
        // Unqualified — try FROM tables first, then global lookup
        for (const [, realTable] of fromTables) {
          const lookup = columnLookup.get(`${realTable.toLowerCase()}.${ref.columnName.toLowerCase()}`);
          if (lookup) {
            resolvedTable = lookup.tableName;
            colInfo = lookup;
            break;
          }
        }
        if (!resolvedTable) {
          const candidates = columnToTables.get(ref.columnName.toLowerCase());
          if (candidates && candidates.length === 1) {
            resolvedTable = candidates[0].tableName;
            colInfo = candidates[0].col;
          }
        }
      }

      if (!resolvedTable || !colInfo) continue;

      const dedupKey = `${resolvedTable.toLowerCase()}.${colInfo.column_name.toLowerCase()}`;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);

      const filterType = detectFilterType(colInfo.data_type, colInfo.column_name);
      const filterId = `auto:${resolvedTable}.${colInfo.column_name}`;

      filters.push({
        id: filterId,
        fieldName: colInfo.column_name,
        tableName: resolvedTable,
        dataType: colInfo.data_type,
        filterType,
        label: toLabel(colInfo.column_name),
        queryId: "",   // auto-detected, not tied to a saved query
        queryName: widget.title || "",
      });
    }
  }

  return filters;
}
